import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { loadImageFromFile, rotateCanvas90, warpPerspective } from './perspectiveWarp';
import { FILTER_MODES, applyFilter } from './imageFilters';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 11000;
    background: rgba(0, 0, 0, 0.72);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
`;

const EditorFrame = styled.div`
    background: var(--bg, #fff);
    border-radius: 0.6rem;
    max-width: 96vw;
    max-height: 94vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 0.6rem 2.5rem rgba(0, 0, 0, 0.45);
`;

const Header = styled.div`
    padding: 1rem 1.4rem;
    font-weight: 700;
    font-size: 1.1rem;
    color: var(--dark-gray);
    background: var(--extra-extra-light-gray, #f5f5f5);
    box-shadow: 0 0.1rem 0.1rem -0.1rem var(--gray);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    span.fileName {
        font-weight: 400;
        font-size: 0.9rem;
        color: var(--light-gray);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 24rem;
    }
`;

const CanvasArea = styled.div`
    position: relative;
    margin: 1rem auto;
    touch-action: none;
    canvas {
        display: block;
        border-radius: 0.25rem;
    }
    svg {
        position: absolute;
        inset: 0;
        overflow: visible;
    }
`;

const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.9rem;
    padding: 0.8rem 1.4rem;
    background: var(--extra-extra-light-gray, #f5f5f5);
    box-shadow: 0 -0.1rem 0.1rem -0.1rem var(--gray);
    label {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        color: var(--dark-gray);
        font-size: 0.95rem;
        cursor: pointer;
    }
    input[type='range'] {
        width: 9rem;
    }
    .spacer {
        flex: 1;
    }
`;

const Button = styled.button`
    border: none;
    border-radius: 0.35rem;
    padding: 0.55rem 1.2rem;
    font-size: 0.95rem;
    cursor: pointer;
    font-family: var(--main-font-family);
    background: ${(props) => (props.$primary ? 'var(--main-color, #30c08c)' : 'var(--medium-light-gray, #d9d9d9)')};
    color: ${(props) => (props.$primary ? '#fff' : 'var(--extra-dark-gray, #111)')};
    font-weight: ${(props) => (props.$primary ? '700' : '600')};
    opacity: ${(props) => (props.disabled ? 0.6 : 1)};
    box-shadow: var(--button-shadow);
    transition: 0.08s;
    &:hover:not(:disabled) {
        filter: brightness(1.08);
    }
`;

const FilterToggle = styled.div`
    display: flex;
    border-radius: 0.4rem;
    overflow: hidden;
    box-shadow: var(--button-outline);
    -moz-box-shadow: var(--button-outline);
    -webkit-box-shadow: var(--button-outline);
    button {
        border: none;
        padding: 0.5rem 1.1rem;
        font-size: 0.95rem;
        font-family: var(--main-font-family);
        cursor: pointer;
        background: transparent;
        color: var(--light-gray);
        font-weight: 600;
        transition: 0.08s;
        &[data-active='true'] {
            background: var(--main-color, #30c08c);
            color: #fff;
            font-weight: 700;
        }
        &:not([data-active='true']):hover {
            color: var(--dark-gray);
        }
    }
`;

const Hint = styled.div`
    padding: 0 1.4rem 0.4rem;
    color: var(--light-gray);
    font-size: 0.85rem;
`;

const HANDLE_RADIUS = 11;

const defaultCornersFor = (width, height) => {
    const insetX = Math.round(width * 0.04);
    const insetY = Math.round(height * 0.04);
    return [
        { x: insetX, y: insetY },
        { x: width - insetX, y: insetY },
        { x: width - insetX, y: height - insetY },
        { x: insetX, y: height - insetY },
    ];
};

const outputFileName = (originalName) => {
    const base = (originalName || 'document').replace(/\.[^.]+$/, '');
    return `${base}_scan.jpg`;
};

/**
 * Fullscreen editor for photographed documents: drag the 4 corners to crop and
 * fix perspective/skew, optionally apply grayscale or scanned black & white,
 * then attach the straightened image.
 */
export const ImageDocumentEditor = ({ file, onComplete, onCancel }) => {
    const [sourceCanvas, setSourceCanvas] = useState(null);
    const [corners, setCorners] = useState(null);
    const [filterMode, setFilterMode] = useState(FILTER_MODES.ORIGINAL);
    // Setter unused while the B&W Scan option is hidden.
    const [threshold] = useState(170);
    const [processing, setProcessing] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const displayCanvasRef = useRef(null);
    const canvasAreaRef = useRef(null);
    const dragIndexRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        setSourceCanvas(null);
        setCorners(null);
        setLoadError(false);
        setProcessing(false);
        setFilterMode(FILTER_MODES.ORIGINAL);
        loadImageFromFile(file)
            .then((canvas) => {
                if (cancelled) return;
                setSourceCanvas(canvas);
                setCorners(defaultCornersFor(canvas.width, canvas.height));
            })
            .catch(() => {
                if (!cancelled) setLoadError(true);
            });
        return () => {
            cancelled = true;
        };
    }, [file]);

    const displayScale = useMemo(() => {
        if (!sourceCanvas) return 1;
        const maxWidth = Math.min(window.innerWidth * 0.9, 880);
        const maxHeight = window.innerHeight * 0.6;
        return Math.min(1, maxWidth / sourceCanvas.width, maxHeight / sourceCanvas.height);
    }, [sourceCanvas]);

    useEffect(() => {
        if (!sourceCanvas || !displayCanvasRef.current) return;
        const canvas = displayCanvasRef.current;
        canvas.width = Math.round(sourceCanvas.width * displayScale);
        canvas.height = Math.round(sourceCanvas.height * displayScale);
        canvas.getContext('2d').drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);
        // Real pixel filter on the scaled preview so mode/intensity changes are visible live.
        applyFilter(canvas, filterMode, threshold);
    }, [sourceCanvas, displayScale, filterMode, threshold]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onCancel();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onCancel]);

    const clampedCorner = useCallback((clientX, clientY) => {
        const rect = canvasAreaRef.current.getBoundingClientRect();
        const x = (clientX - rect.left) / displayScale;
        const y = (clientY - rect.top) / displayScale;
        return {
            x: Math.min(Math.max(0, x), sourceCanvas.width),
            y: Math.min(Math.max(0, y), sourceCanvas.height),
        };
    }, [displayScale, sourceCanvas]);

    const onHandlePointerDown = (index) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragIndexRef.current = index;
        e.target.setPointerCapture?.(e.pointerId);
    };
    const onHandlePointerMove = (e) => {
        if (dragIndexRef.current === null || !sourceCanvas) return;
        const index = dragIndexRef.current;
        const next = clampedCorner(e.clientX, e.clientY);
        setCorners((prev) => prev.map((corner, i) => (i === index ? next : corner)));
    };
    const onHandlePointerUp = () => {
        dragIndexRef.current = null;
    };

    const rotate = () => {
        if (!sourceCanvas) return;
        const rotated = rotateCanvas90(sourceCanvas);
        setSourceCanvas(rotated);
        setCorners(defaultCornersFor(rotated.width, rotated.height));
    };

    const resetCorners = () => {
        if (!sourceCanvas) return;
        setCorners(defaultCornersFor(sourceCanvas.width, sourceCanvas.height));
    };

    const apply = () => {
        if (!sourceCanvas || !corners || processing) return;
        setProcessing(true);
        // Let the button repaint into its disabled state before the sync warp.
        requestAnimationFrame(() => {
            try {
                const warped = warpPerspective(sourceCanvas, corners);
                if (!warped) throw new Error('warp failed');
                applyFilter(warped, filterMode, threshold);
                warped.toBlob(
                    (blob) => {
                        if (!blob) {
                            setProcessing(false);
                            return;
                        }
                        onComplete(new File([blob], outputFileName(file.name), { type: 'image/jpeg' }));
                    },
                    'image/jpeg',
                    0.92,
                );
            } catch (error) {
                setProcessing(false);
                onComplete(file); // fall back to the untouched image rather than losing the upload
            }
        });
    };

    const scaledCorners = corners
        ? corners.map(({ x, y }) => ({ x: x * displayScale, y: y * displayScale }))
        : [];
    const polygonPoints = scaledCorners.map(({ x, y }) => `${x},${y}`).join(' ');

    return (
        <Overlay onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
            <EditorFrame>
                <Header>
                    Prepare Document
                    <span className="fileName">{file?.name}</span>
                </Header>
                {loadError ? (
                    <Hint style={{ color: 'var(--alert-color)', padding: '2rem 1.4rem' }}>
                        This image could not be opened.
                    </Hint>
                ) : (
                    <CanvasArea
                        ref={canvasAreaRef}
                        onPointerMove={onHandlePointerMove}
                        onPointerUp={onHandlePointerUp}
                    >
                        <canvas ref={displayCanvasRef} />
                        {corners ? (
                            <svg
                                width={Math.round((sourceCanvas?.width || 0) * displayScale)}
                                height={Math.round((sourceCanvas?.height || 0) * displayScale)}
                            >
                                <polygon
                                    points={polygonPoints}
                                    fill="rgba(48, 192, 140, 0.14)"
                                    stroke="var(--main-color, #30c08c)"
                                    strokeWidth="2"
                                />
                                {scaledCorners.map(({ x, y }, index) => (
                                    <circle
                                        key={index}
                                        cx={x}
                                        cy={y}
                                        r={HANDLE_RADIUS}
                                        fill="var(--main-color, #30c08c)"
                                        stroke="#fff"
                                        strokeWidth="2.5"
                                        style={{ cursor: 'grab' }}
                                        onPointerDown={onHandlePointerDown(index)}
                                    />
                                ))}
                            </svg>
                        ) : null}
                    </CanvasArea>
                )}
                <Controls>
                    <FilterToggle>
                        <button
                            type="button"
                            data-active={filterMode === FILTER_MODES.ORIGINAL}
                            onClick={() => setFilterMode(FILTER_MODES.ORIGINAL)}
                        >
                            Original
                        </button>
                        <button
                            type="button"
                            data-active={filterMode === FILTER_MODES.GRAYSCALE}
                            onClick={() => setFilterMode(FILTER_MODES.GRAYSCALE)}
                        >
                            Grayscale
                        </button>
                        {/* B&W Scan option hidden for now; filter logic kept in imageFilters.js. */}
                    </FilterToggle>
                    <div className="spacer" />
                    <Button type="button" onClick={rotate} disabled={!sourceCanvas}>Rotate</Button>
                    <Button type="button" onClick={resetCorners} disabled={!sourceCanvas}>Reset</Button>
                    <Button type="button" onClick={onCancel}>Cancel</Button>
                    <Button type="button" $primary onClick={apply} disabled={!sourceCanvas || processing}>
                        {processing ? 'Processing…' : 'Apply & Attach'}
                    </Button>
                </Controls>
            </EditorFrame>
        </Overlay>
    );
};
